import { Component, OnInit } from '@angular/core';
import { DirectoryUser, SchoolDirectory } from '../shared/models/school.directory';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { ApiService } from '../shared/services/api.service';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { MobileUiService } from '../shared/services/mobile-ui.service';

export interface DirectoryListing {
  profile: DirectoryUser;
  department: string;
  school: string;
}

export interface DirectoryFilter {
  schools: string[];
  departments: string[];
}

@Component({
  selector: 'app-directory',
  templateUrl: './directory.component.html',
  styleUrls: ['./directory.component.scss']
})
export class DirectoryComponent implements OnInit {
  directory = new BehaviorSubject<DirectoryListing[]>(null);
  directoryClean: DirectoryListing[];
  directoryFilter = new BehaviorSubject<DirectoryFilter>(null);
  appliedFilter = new BehaviorSubject<string>(null);
  selectedSchool = '';
  schoolToDepartmentsMap = new Map<string, string[]>();
  searchField = new FormControl();
  faSearch = faSearch;
  faTimes = faTimes;
  page = 1;
  pageSize = 10;
  isMobile = new BehaviorSubject<boolean>(false);
  isLoading = true;
  skeletonLoaders = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private mobileUiService: MobileUiService) { this.isMobile = this.mobileUiService.isMobile; }

  ngOnInit(): void {
    this.getDirectory();
  }

  autoComplete = (text: Observable<string>) =>
    text.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => term.length < 2 ? []
        : this.directoryClean.filter(user => user.profile.firstname.toLowerCase().trim().includes(term.toLowerCase())
          || user.profile.lastname.toLowerCase().trim().includes(term.toLowerCase())).slice(0, 10)
      )
    )

  autoCompleteFormatter = (result: DirectoryListing) => result.profile?.preferredName ?? `${result.profile.firstname} ${result.profile.lastname}`;

  search(byDept: boolean, bySchool: boolean, filter: string | DirectoryListing): void {
    // Clear previous filters first
    this.appliedFilter.next(null);
    this.directory.next(this.directoryClean);

    if (bySchool && typeof filter === 'string') {
      this.selectedSchool = this.getSchoolKey(filter.toString());
    }

    let term = byDept || bySchool ? filter : this.searchField.value;

    // This is an autocomplete selection
    // Filter is a DirectoryListing object
    if (typeof filter === 'object') {
      term = `${filter.profile.firstname} ${filter.profile.lastname}`;
    }

    if (term.toString().trim().length < 1) {
      return;
    } else {
      const result = this.filter(term, byDept, bySchool);
      if (result.length > 0) {
        this.directory.next(result);
      } else {
        this.getDirectory();
        // if (this.searchField.value.length < 1) {
        //   // Handle situation where results are null
        // }
      }
    }

    this.appliedFilter.next(term);
  }

  getAvailableDepartments(): string[] {
    const allDepartments = this.directoryFilter.value?.departments ?? [];

    if (!this.selectedSchool) {
      return allDepartments;
    }

    return this.schoolToDepartmentsMap.get(this.selectedSchool) ?? [];
  }

  clearFilter(): void {
    // Need to clear any params sent through another page
    // This should allow remaining filters to be in place
    this.router.navigate(['/directory'], { queryParams: null, skipLocationChange: true });
    this.searchField.setValue(null);
    this.selectedSchool = '';
    this.appliedFilter.next(null);
    this.getDirectory();
  }

  private filter(value: string, byDept: boolean, bySchool: boolean): DirectoryListing[] {
    if (value.toString().trim().length < 1) {
      return [];
    }

    let results: DirectoryListing[];
    const filterValue = value.toString().trim().toLowerCase().replace('%20', ' ');

    // Filter by first or last name
    if (!byDept && !bySchool) {
      results = this.directory.value
        .filter(user => user.profile.firstname.toLowerCase().trim().includes(filterValue)
          || user.profile.lastname.toLowerCase().trim().includes(filterValue));
    }

    // Filter by Full Name
    if (!byDept && !bySchool) {
      results = this.directory.value
        .filter(user => `${user.profile.firstname.toLowerCase().trim()} ${user.profile.lastname.toLowerCase().trim()}` === filterValue);
    }

    // Filter by school
    if (bySchool) {
      results = this.directory.value.filter(item => item.school.trim().toLowerCase() === filterValue);
    }

    // Filter by department
    if (byDept) {
      results = this.directory.value.filter(item => item.department.trim().toLowerCase() === filterValue);
    }

    // Sort results by last name
    results = results.sort(this.compareObjects);
    return results;
  }

  private getDirectory(): void {
    this.isLoading = true;
    this.apiService.getDirectory().subscribe((dir: SchoolDirectory) => {
      let directory = [] as DirectoryListing[];
      const directoryFilter = {} as DirectoryFilter;
      directoryFilter.departments = [];
      directoryFilter.schools = [];
      const departmentSet = new Set<string>();
      const schoolSet = new Set<string>();
      this.schoolToDepartmentsMap = new Map<string, string[]>();

      dir.schools.forEach(school => {
        schoolSet.add(school.name);
        const schoolDepartmentSet = new Set<string>();

        school.departments.forEach(dept => {
          departmentSet.add(dept.name);
          schoolDepartmentSet.add(dept.name);

          dept.users.forEach(user => {
            directory.push({ profile: user, department: dept.name, school: school.name });
          });
        });

        this.schoolToDepartmentsMap.set(this.getSchoolKey(school.name), Array.from(schoolDepartmentSet).sort());
      });

      directoryFilter.departments = Array.from(departmentSet);
      directoryFilter.schools = Array.from(schoolSet);

      directory = directory.sort(this.compareObjects);

      // Sort Directory Filters
      directoryFilter.departments.sort();
      directoryFilter.schools.sort();

      this.directory.next(directory);
      this.directoryClean = directory;
      this.directoryFilter.next(directoryFilter);

      // Catch filters in route
      this.route.params.subscribe(params => {
        if (params.filter != null) {
          if (params.filter.toString().includes('school=')) {
            this.search(false, true, params.filter.toString().split('school=')[1]);
          }

          if (params.filter.toString().includes('department=')) {
            this.search(true, false, params.filter.toString().split('department=')[1]);
          }
        }
      });

      this.isLoading = false;
    });
  }

  private compareObjects(a: DirectoryListing, b: DirectoryListing): number {
    if (a.profile.lastname < b.profile.lastname) {
      return -1;
    }
    if (a.profile.lastname > b.profile.lastname) {
      return 1;
    }
  }

  private getSchoolKey(schoolName: string): string {
    return schoolName?.trim().toLowerCase() ?? '';
  }
}

